import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLanguageCode } from '../../../store/app/appStore';

interface AutoTextfitProps {
  children: React.ReactNode;
  maxFontSize?: number;
  minFontSize?: number;
  className?: string;
}

const TextContainer = styled.div<{ fontSize: number }>`
  font-size: ${({ fontSize }) => fontSize}px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AutoTextfit: React.FC<AutoTextfitProps> = ({ 
  children, 
  maxFontSize = 16, 
  minFontSize = 10,
  className 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState(maxFontSize);
  const languageCode = useLanguageCode();

  useEffect(() => {
    const fitText = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const parent = container.parentElement;
      if (!parent) return;

      // Reset font size to max and force reflow to get accurate measurements
      let currentSize = maxFontSize;
      container.style.fontSize = `${currentSize}px`;
      const parentWidth = parent.clientWidth - 8; // Subtracting 8px for save area

      while (
        currentSize > minFontSize &&
        (container.scrollWidth > parentWidth || container.scrollHeight > parent.clientHeight)
      ) {
        currentSize -= 1;
        container.style.fontSize = `${currentSize}px`;
      }

      setFontSize(currentSize);
    };

    // Add small timeout as fallback for async translation loading
    setTimeout(() => {
      fitText();
    }, 100);

    window.addEventListener('resize', fitText);
    return () => window.removeEventListener('resize', fitText);
  }, [children, maxFontSize, minFontSize, languageCode]);

  return (
    <TextContainer ref={containerRef} fontSize={fontSize} className={className}>
      {children}
    </TextContainer>
  );
};

export default AutoTextfit;